import {
  afterRenderEffect,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {DatePipe, NgClass, NgTemplateOutlet} from '@angular/common';
import {Tooltip} from 'primeng/tooltip';
import {ConfirmationService} from 'primeng/api';
import {TaCommentCount} from './ta-comment-count';
import {TaCommentInputComponent} from './ta-comment-input/ta-comment-input';
import {TaCommentTextComponent} from './ta-comment-text';
import {TaWorkflowTagComponent} from './ta-workflow-tag';
import {TaSpinnerComponent} from '../taspinner.component/taspinner.component';
import {TaCommentsDatasource} from '../../../datasources/ta-comments.datasource';
import {TaAppStore} from '../../../state/clcta-store';
import {ErrorMessageService} from '../../../services/error-message.service';
import {TaCommentDetails} from '../../../api';
import {ALL_TA_COMMENT_CAPABILITIES, TaCommentCapabilities} from '../../../util/app.types';
import {
  commentAuthorId,
  commentAuthorName,
  commentAuthorTooltip,
  commentInitials,
  isCommentRemoved,
  MentionPerson,
  splitWorkflowTag,
  withWorkflowTag,
} from '../../../util/ta-comment.util';

export type TaCommentsMode = 'inline' | 'collapsible';

/** What the expanded panel keeps clear of the bottom of the window - the page's own p-6. */
const PANEL_BOTTOM_GAP_PX = 24;

/** The expanded panel's own p-5, so the chain inside it stops short of its bottom edge. */
const PANEL_PADDING_PX = 20;

/**
 * What the chain leaves clear below itself unless a host says otherwise: enough for the expanded
 * panel's padding and the gap it keeps between itself and the bottom of the window.
 */
const DEFAULT_BOTTOM_GAP_PX = PANEL_BOTTOM_GAP_PX + PANEL_PADDING_PX;

/**
 * The shortest the chain will make itself. Below this it stops giving up height and lets its host
 * scroll instead - a chain squeezed to a line or two is worse than a card with a scrollbar.
 */
const MIN_CHAIN_HEIGHT_PX = 160;

/**
 * The panel's own w-[38rem] max-w-[calc(100vw-2rem)], in numbers. Only the opening animation needs
 * them: the panel shares its right edge with the pill, so how far the heading has to travel from
 * the pill's position to the panel's is the difference between the two widths, and that has to be
 * known before the panel exists to be measured.
 */
const PANEL_WIDTH_PX = 608;
const PANEL_VIEWPORT_MARGIN_PX = 32;

/**
 * The TA comment chain, heading and all - the one component behind both entry points: the bread bar
 * on the create, review and manage screens (collapsible), and the TA info card (inline). It owns
 * its own fetch, the way ta-audit-log does, rather than reading the chain out of the store: the
 * info card re-points at whichever TA the user clicks, so the chain follows `taId` rather than the
 * loaded TA. The fetch waits until the chain is on screen, and the count in the heading comes off
 * the TA until then - see commentCount - so a collapsed bar costs no request.
 *
 * The two modes render the same block; collapsible adds the pill and the backdrop it expands over.
 */
@Component({
  selector: 'ta-comments',
  imports: [
    DatePipe,
    NgClass,
    NgTemplateOutlet,
    TaCommentCount,
    TaCommentInputComponent,
    TaCommentTextComponent,
    TaWorkflowTagComponent,
    TaSpinnerComponent,
    Tooltip,
  ],
  templateUrl: './ta-comments.html',
  styleUrl: './ta-comments.scss',
  host: {
    // Escape closes the panel the same way the backdrop does. On the document rather than the host:
    // once open, focus is in the composer inside a fixed panel, not necessarily under this element.
    '(document:keydown.escape)': 'collapse()',
    // Both measurements are in viewport coordinates, off boxes that move when the window does.
    '(window:resize)': 'measure()',
  },
})
export class TaCommentsComponent {
  private commentsDatasource = inject(TaCommentsDatasource);
  private confirmationService = inject(ConfirmationService);
  private errorMessageService = inject(ErrorMessageService);
  private store = inject(TaAppStore);

  /** The TA whose chain to show. Undefined while a host is still resolving its TA. */
  taId = input<string | undefined>(undefined);
  /**
   * How much room the chain leaves below itself, in pixels: whatever stands between it and the
   * bottom of the window - its host card's padding, and any gap the host keeps clear of the edge.
   * The chain takes everything above that, so the block it sits in reaches down to the same line
   * whether the conversation is two comments or two hundred.
   */
  bottomGap = input<number>(DEFAULT_BOTTOM_GAP_PX);
  /**
   * `inline` draws the chain where it stands and folds it away under its own heading - the TA info
   * card, whose summary above it folds the same way. `collapsible` draws the heading as a pill and
   * expands the same block over the page, which is what the bread bar needs: the bar is one row
   * tall and has nowhere to put a conversation.
   */
  mode = input<TaCommentsMode>('inline');
  /**
   * What the user is allowed to do to this chain - see TaCommentCapabilities. The component takes
   * the answer rather than working it out: the criteria are the host's business (TA status today),
   * and the server enforces them regardless. Defaults to everything allowed.
   */
  capabilities = input<TaCommentCapabilities>(ALL_TA_COMMENT_CAPABILITIES);

  private commentInput = viewChild<TaCommentInputComponent>('commentInput');
  private collapsedAnchor = viewChild<ElementRef<HTMLElement>>('collapsedAnchor');
  private chain = viewChild<ElementRef<HTMLElement>>('chain');
  /** Set when the panel opens, cleared once the composer has been focused for that open. */
  private focusOnRender = false;

  expanded = signal<boolean>(false);
  /**
   * Inline mode's fold. Kept apart from `expanded`: that one opens a panel over the page and drags
   * the backdrop, the measurements and Escape along with it, none of which a folded section wants.
   */
  inlineCollapsed = signal<boolean>(false);
  /** Where the expanded panel sits: measured off the collapsed pill, so it covers it exactly. */
  panelTop = signal<number>(0);
  panelRight = signal<number>(0);
  /** How far the panel's heading starts to the right of where it ends up. See ta-comments.scss. */
  headingSlide = signal<string>('0px');
  /** The height of the scrolling chain, measured off where the chain actually sits. */
  chainHeight = signal<string>(`${MIN_CHAIN_HEIGHT_PX}px`);

  comments = signal<TaCommentDetails[]>([]);
  /** The TA `comments` was fetched for. Undefined until a chain has actually landed. */
  private loadedTaId = signal<string | undefined>(undefined);
  loading = signal<boolean>(false);
  /** Set while a post / edit / remove is outstanding - blocks a second one and the composer. */
  saving = signal<boolean>(false);

  /** True while the composer has focus - keeps the send button live on an empty box. */
  composerFocused = signal<boolean>(false);

  /**
   * The comment currently open in the inline editor, its working copy, and the workflow tag key
   * the copy was split from. The draft is the *stored* form - @mentions as `@[Name](id)` tokens -
   * which is what the box works in and posts, minus the tag: that never enters the box and goes
   * back on in saveEdit.
   */
  editingId = signal<string | undefined>(undefined);
  editDraft = signal<string>('');
  editingTag = signal<string | undefined>(undefined);

  newMessage = signal<string>('');

  private currentUserId = computed<string | undefined>(
    () => this.store.session().details?.user?.id ?? undefined);

  hasComments = computed<boolean>(() => this.comments().length > 0);
  /** Whether an own comment has any control under it at all - neither allowed drops the row. */
  showOwnCommentActions = computed<boolean>(() =>
    this.capabilities().editing || this.capabilities().deleting);
  /** Whether the composer and chain are showing, in whichever mode is drawing them. */
  showBody = computed<boolean>(() =>
    this.mode() === 'collapsible' ? this.expanded() : !this.inlineCollapsed());
  /**
   * Who can be @mentioned, as the server scopes it to this TA. Feeds the composer's picker and
   * nothing else - a mention already in a comment carries its own name, so the chain renders
   * whether or not this has landed, and a failed load costs the picker rather than the page.
   * Empty until the chain is shown: see loadMentionList.
   */
  mentionPeople = signal<MentionPerson[]>([]);
  /** Set while that list is in flight, so an @ typed before it lands opens on a loading row. */
  mentionPeopleLoading = signal<boolean>(false);
  /**
   * Which mention request owns the signals above. Bumped per request, so a slow one that has been
   * superseded - the chain reopened, or re-pointed at another TA - drops its response instead of
   * overwriting a fresher list.
   */
  private mentionRequestId = 0;
  /** Whether `comments` holds this TA's chain rather than nothing fetched yet. */
  private chainLoaded = computed<boolean>(() => !!this.taId() && this.loadedTaId() === this.taId());
  /**
   * The count the server put on the TA itself, for a chain that has not been fetched. Guarded on
   * the id: a host pointing this at something other than the loaded TA gets nothing rather than
   * another TA's number.
   */
  private taDetailsCount = computed<number>(() => {
    const ta = this.store.taSubmission();
    return !!ta?.id && ta.id === this.taId() ? ta.commentCount ?? 0 : 0;
  });
  /**
   * What the heading badge counts: the chain once it is here, and the TA's own count until then -
   * which is what lets the collapsed bar show a number without fetching the conversation behind it.
   * Tombstones are counted on both sides - the server counts deleted comments too - so the number
   * does not move as the chain lands.
   */
  commentCount = computed<number>(() =>
    this.chainLoaded() ? this.comments().length : this.taDetailsCount());
  /**
   * Keeps the panel inside the window however far down the page its anchor sits. The chain fills
   * the same room, so this is the line the panel actually lands on rather than a cap it rarely met.
   */
  panelMaxHeight = computed<string>(() => `calc(100vh - ${this.panelTop() + PANEL_BOTTOM_GAP_PX}px)`);

  constructor() {
    // An effect rather than ngOnInit: the info card keeps one instance mounted and swaps taId as
    // the user moves down the list, and the bread bar's copy can mount before its TA lands.
    effect(() => {
      const taId = this.taId();
      this.editingId.set(undefined);
      // A new TA is a new conversation: nothing typed for the last one carries into it, and it
      // opens rather than staying folded.
      this.editDraft.set('');
      this.editingTag.set(undefined);
      this.newMessage.set('');
      this.inlineCollapsed.set(false);
      // The picker's list belongs to the TA it was fetched for, so every TA change clears it
      // rather than replacing it. Clears the flag too: a request still in flight for the last TA
      // bails on arrival, so nothing else would take it down.
      this.mentionPeople.set([]);
      this.mentionPeopleLoading.set(false);
      // The chain goes with it, unfetched: a request still in flight for the last TA bails on
      // arrival, so nothing else would clear these, and the last TA's conversation must not sit
      // under this one's heading. The effect below fetches the new one when it is on screen.
      this.loading.set(false);
      this.comments.set([]);
      this.loadedTaId.set(undefined);
    });

    // The fetch waits for the chain to actually be on screen. Collapsible mode shows only a count
    // in the bar, and that comes off the TA, so a TA that is opened and never expanded costs no
    // request; inline mode draws the chain as it stands, so this follows the reset above straight
    // away. Runs again on the next open after a failed load - loadedTaId is only set by a chain
    // that arrived.
    effect(() => {
      const taId = this.taId();
      const showBody = this.showBody();
      untracked(() => {
        if (!taId || !showBody || this.loading() || this.loadedTaId() === taId) {
          return;
        }
        this.loadComments(taId);
      });
    });

    // The picker's list, re-requested every time the chain is shown: the pill expanding, the
    // inline section unfolding, or the info card re-pointing at another TA while it is already
    // open. Not cached and not fetched once per TA - who can be mentioned follows the TA's
    // traveller list, and on the create and review screens that list is being edited a few
    // controls away, so a list from an earlier showing can be missing someone just added.
    // Keyed on the chain being visible rather than on a box being focused: it is the earliest
    // point the user has committed to reading the conversation, which leaves the round trip the
    // most room to finish before the @ that opens the picker.
    effect(() => {
      const taId = this.taId();
      const showing = this.showBody();
      untracked(() => {
        if (taId && showing) {
          this.loadMentionList(taId);
        }
      });
    });

    // An editor left open when the host takes editing away closes itself: a TA's status can
    // change while the chain is on screen, and a save from there would only be refused.
    effect(() => {
      if (!this.capabilities().editing && untracked(this.editingId)) {
        this.cancelEdit();
      }
    });

    // After render, not in expand(): the composer is only created as the panel opens, so it does
    // not exist yet at the click. Reads expanded() so it re-runs on the open that created it.
    afterRenderEffect(() => {
      if (!this.expanded() || !this.focusOnRender) {
        return;
      }
      const input = this.commentInput();
      if (!input) {
        return;
      }
      this.focusOnRender = false;
      input.focus();
    });

    // The chain's height depends on where it ends up, so it is re-measured after render, and read
    // off everything that moves it: the panel opening, the fold, the comments arriving, the editor
    // opening on one. chainHeight is written untracked, so this settles.
    afterRenderEffect(() => {
      this.expanded();
      this.inlineCollapsed();
      this.comments();
      this.editingId();
      this.loading();
      this.bottomGap();
      // The composer sits above the chain, so a host taking it away moves the chain up.
      this.capabilities();
      this.measureChain();
    });
  }

  expand(): void {
    if (!this.taId()) {
      return;
    }
    this.measureAnchor();
    this.focusOnRender = true;
    this.expanded.set(true);
  }

  collapse(): void {
    this.expanded.set(false);
    this.focusOnRender = false;
  }

  /** Inline mode's chevron. Folds the block down to its heading row and back. */
  toggleInline(): void {
    this.inlineCollapsed.update(collapsed => !collapsed);
  }

  /**
   * Re-measures once a fold has finished moving. The fold's rows grow after the click-time
   * measure, so the room measured then is stale by the time the fold has settled - and the chain is
   * what holds the card open to the bottom of the page, so a stale measurement is a card that
   * overruns the page and grows a scrollbar. The top padding is held constant so the heading
   * doesn't move. Filtered to the fold's row transition: transitionend bubbles, and the buttons
   * inside have transitions of their own.
   */
  onFoldSettled(event: TransitionEvent): void {
    if (event.propertyName !== 'grid-template-rows') {
      return;
    }
    this.measureChain();
  }

  /**
   * Re-reads both measurements. Public because the host listens for window resizes: the panel is
   * positioned in viewport coordinates and the chain is capped in them, so both follow the window.
   */
  measure(): void {
    this.measureAnchor();
    this.measureChain();
  }

  /**
   * Sizes the chain to the room between where it sits and the bottom of the window. A height rather
   * than a cap: the chain is the block's one stretchy part, so giving it the whole of what is left
   * is what carries the panel - and the info card around it - down to the bottom of the page,
   * whether there are two comments or two hundred. Floored at a readable few: a chain pushed low by
   * a tall card stops shrinking there and lets the card scroll instead.
   */
  measureChain(): void {
    const chain = this.chain()?.nativeElement;
    // A hidden element measures as zero, which would cap the chain at nearly the whole window -
    // taller than the room it has. Keep the last good value; the next render measures again.
    if (!chain || chain.getClientRects().length === 0) {
      return;
    }
    const room = window.innerHeight - chain.getBoundingClientRect().top - this.bottomGap();
    // Floor, not round: a host sized to the same bottom line is one rounded-up pixel away from a
    // scrollbar it should not have.
    const next = `${Math.max(MIN_CHAIN_HEIGHT_PX, Math.floor(room))}px`;
    if (next !== untracked(this.chainHeight)) {
      this.chainHeight.set(next);
    }
  }

  /**
   * Re-reads where the collapsed pill sits. The panel is positioned in viewport coordinates, so it
   * has to follow the bar as it moves.
   */
  measureAnchor(): void {
    const anchor = this.collapsedAnchor()?.nativeElement;
    if (!anchor) {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    this.panelTop.set(rect.top);
    this.panelRight.set(Math.max(window.innerWidth - rect.right, 0));

    const panelWidth = Math.min(PANEL_WIDTH_PX, window.innerWidth - PANEL_VIEWPORT_MARGIN_PX);
    this.headingSlide.set(`${Math.max(panelWidth - rect.width, 0)}px`);
  }

  isOwnComment(comment: TaCommentDetails): boolean {
    const userId = this.currentUserId();
    return !!userId && commentAuthorId(comment) === userId;
  }

  /** Guarded on the id: two comments the server sent without one must not both open the editor. */
  isEditing(comment: TaCommentDetails): boolean {
    return !!comment.id && this.editingId() === comment.id;
  }

  /**
   * Whether the send button is live. Focus counts as well as typed text, so the button reads as the
   * composer's action from the moment the user clicks into the box rather than appearing to arrive
   * with the first keystroke; addComment() still refuses an empty message.
   */
  canSend(): boolean {
    return !!this.taId() && this.capabilities().adding && !this.saving()
      && (this.composerFocused() || this.newMessage().trim().length > 0);
  }

  /** The composer's focus, which keeps the send button live on an empty box. */
  onComposerFocus(focused: boolean): void {
    this.composerFocused.set(focused);
  }

  canSaveEdit(): boolean {
    return this.capabilities().editing && !this.saving() && this.editDraft().trim().length > 0;
  }

  addComment(): void {
    const taId = this.taId();
    const message = this.newMessage().trim();
    // Not canSend(): that is live on an empty focused box, and there is nothing to post from one.
    if (!taId || !this.capabilities().adding || this.saving() || !message) {
      return;
    }
    this.saving.set(true);
    this.commentsDatasource.addTaComment(taId, message).subscribe({
      next: response => {
        this.saving.set(false);
        if (response.success && response.data) {
          // Cleared only on success, so a failed post leaves the user their text to retry with.
          this.newMessage.set('');
          // Newest first, so a posted comment lands at the top next to the composer.
          this.comments.update(comments => [response.data!, ...comments]);
          this.syncTaDetailsCount();
        } else {
          this.errorMessageService.showTemporaryError(
            response.error ?? 'Your comment could not be added.', 5000);
        }
      },
      error: error => {
        this.saving.set(false);
        console.error('addTaComment error:', error);
        this.errorMessageService.showTemporaryError('Your comment could not be added.', 5000);
      },
    });
  }

  startEdit(comment: TaCommentDetails): void {
    if (!comment.id || !this.capabilities().editing) {
      return;
    }
    const {key, body} = splitWorkflowTag(comment.text);
    this.editingTag.set(key);
    this.editDraft.set(body);
    this.editingId.set(comment.id);
  }

  cancelEdit(): void {
    this.editingId.set(undefined);
    this.editDraft.set('');
    this.editingTag.set(undefined);
  }

  saveEdit(comment: TaCommentDetails): void {
    const taId = this.taId();
    const commentId = comment.id;
    if (!taId || !commentId || !this.canSaveEdit()) {
      return;
    }
    const message = withWorkflowTag(this.editingTag(), this.editDraft().trim());
    this.saving.set(true);
    this.commentsDatasource.editTaComment(taId, commentId, message).subscribe({
      next: response => {
        this.saving.set(false);
        if (response.success && response.data) {
          this.replaceComment(response.data);
          this.cancelEdit();
        } else {
          this.errorMessageService.showTemporaryError(
            response.error ?? 'Your comment could not be updated.', 5000);
        }
      },
      error: error => {
        this.saving.set(false);
        console.error('editTaComment error:', error);
        this.errorMessageService.showTemporaryError('Your comment could not be updated.', 5000);
      },
    });
  }

  /**
   * Removing is not undoable and the tombstone it leaves is visible to everyone on the TA, so it
   * goes through the shared confirm dialog rather than deleting on the click.
   */
  removeComment(comment: TaCommentDetails): void {
    const taId = this.taId();
    const commentId = comment.id;
    if (!taId || !commentId || !this.capabilities().deleting || this.saving()) {
      return;
    }
    this.confirmationService.confirm({
      // The shared dialog renders its message as sanitised HTML, so the two paragraphs are markup.
      message: '<p>Are you sure you want to delete this comment?</p>'
        + '<p class="mt-3">Other users will be able to see that a comment was deleted.</p>',
      header: 'Delete this comment permanently?',
      icon: '',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger',
      },
      accept: () => this.sendRemove(taId, commentId),
      reject: () => {},
    });
  }

  private sendRemove(taId: string, commentId: string): void {
    this.saving.set(true);
    this.commentsDatasource.removeTaComment(taId, commentId).subscribe({
      next: response => {
        this.saving.set(false);
        if (response.success && response.data) {
          this.replaceComment(response.data);
          if (this.editingId() === commentId) {
            this.cancelEdit();
          }
        } else {
          this.errorMessageService.showTemporaryError(
            response.error ?? 'Your comment could not be removed.', 5000);
        }
      },
      error: error => {
        this.saving.set(false);
        console.error('removeTaComment error:', error);
        this.errorMessageService.showTemporaryError('Your comment could not be removed.', 5000);
      },
    });
  }

  private loadComments(taId: string): void {
    this.loading.set(true);
    this.commentsDatasource.loadTaComments(taId).subscribe({
      next: response => {
        // The host re-points taId as the user moves down a list, so a slow response can arrive
        // after the next TA's request went out. Drop it rather than show it under the wrong TA -
        // the request still in flight owns loading() and the chain.
        if (taId !== this.taId()) {
          return;
        }
        this.loading.set(false);
        if (response.success && response.data) {
          this.comments.set(this.sortedBySentDate(response.data));
          this.loadedTaId.set(taId);
          this.syncTaDetailsCount();
        } else {
          this.comments.set([]);
          console.error('loadTaComments error:', response.error);
          this.errorMessageService.showTemporaryError(
            response.error ?? 'Comments could not be loaded.', 5000);
        }
      },
      error: error => {
        if (taId !== this.taId()) {
          return;
        }
        this.loading.set(false);
        this.comments.set([]);
        console.error('loadTaComments error:', error);
        this.errorMessageService.showTemporaryError('Comments could not be loaded.', 5000);
      },
    });
  }

  /**
   * The picker's list for this TA, fetched fresh every time the chain is shown - see the effect
   * that drives it. Not tied to loading(): the chain is what the user is waiting for, and a
   * mention already in a comment carries its own name, so the conversation reads either way.
   *
   * The list is cleared as the request goes out rather than left in place behind it: the reason
   * this is re-requested at all is that the old one may be missing someone, so the picker showing
   * its loading row for a moment beats it offering a list that is quietly short a name.
   *
   * A failure leaves the list empty and is retried the next time the chain is shown - mentions
   * cannot be picked until it lands, everything else about the box still works.
   */
  private loadMentionList(taId: string): void {
    const requestId = ++this.mentionRequestId;
    this.mentionPeople.set([]);
    this.mentionPeopleLoading.set(true);
    this.commentsDatasource.loadMentionList(taId).subscribe({
      next: response => {
        // Same stale-response guard as loadComments, plus the request id: the picker must not
        // offer the last TA's people, nor an older list for this one that has been superseded.
        if (requestId !== this.mentionRequestId || taId !== this.taId()) {
          return;
        }
        this.mentionPeopleLoading.set(false);
        if (response.success && response.data) {
          this.mentionPeople.set(response.data
            .filter(user => !!user.id && !!user.name)
            .map(user => ({
              id: user.id!,
              name: user.name!,
              email: user.email ?? undefined,
            })));
        } else {
          this.mentionPeople.set([]);
          console.error('loadMentionList error:', response.error);
        }
      },
      error: error => {
        if (requestId !== this.mentionRequestId || taId !== this.taId()) {
          return;
        }
        this.mentionPeopleLoading.set(false);
        this.mentionPeople.set([]);
        console.error('loadMentionList error:', error);
      },
    });
  }

  /**
   * Writes the chain's length back onto the loaded TA, so the count in the collapsed bar - and
   * anywhere else reading the TA - moves with the conversation rather than staying whatever the
   * page load fetched. Guarded on the id, and skipped when the number has not moved, so a chain
   * landing on an unchanged count patches nothing. Called only where the length can change: a post,
   * and the chain arriving. An edit or a delete leaves it alone - a deleted comment stays in the
   * chain as a tombstone and the server counts it too.
   */
  private syncTaDetailsCount(): void {
    const taId = this.taId();
    const ta = this.store.taSubmission();
    const count = this.comments().length;
    if (!taId || ta?.id !== taId || ta.commentCount === count) {
      return;
    }
    this.store.updateTaSubmissionPartial({commentCount: count});
  }

  private replaceComment(updated: TaCommentDetails): void {
    this.comments.update(comments =>
      comments.map(comment => comment.id === updated.id ? updated : comment));
  }

  /**
   * Newest first. The chain is capped and scrolled, so the latest comment has to be the one already
   * in view under the composer rather than the one the user has to scroll to find.
   */
  private sortedBySentDate(comments: TaCommentDetails[]): TaCommentDetails[] {
    return [...comments].sort((a, b) => {
      const sentA = a.creation?.created ? new Date(a.creation.created).getTime() : 0;
      const sentB = b.creation?.created ? new Date(b.creation.created).getTime() : 0;
      return sentB - sentA;
    });
  }

  protected readonly commentAuthorName = commentAuthorName;
  protected readonly commentAuthorTooltip = commentAuthorTooltip;
  protected readonly commentInitials = commentInitials;
  protected readonly isCommentRemoved = isCommentRemoved;
}
